import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/Services/api.service';
import { Busyable } from 'src/app/shared/editors/busyable';
import { ChartDataResponse } from 'src/app/shared/charts/dashboardchart/ChartResponse';

interface CitationStatisticsResponse {
  summary: CitationSummaryStatistics;
  statusBreakdown: CitationStatusBreakdown;
  officerPerformance: OfficerPerformanceStatistics[];
  revenueOverTime: CitationTimeSeries;
  citationsOverTime: CitationTimeSeries;
  citationsByInfringementType: CitationsByInfringementType[];
}

interface CitationSummaryStatistics {
  totalCitations: number;
  totalPaid: number;
  totalVoided: number;
  totalVoidedCountsAgainstPerformance: number;
  totalPending: number;
  totalRevenue: number;
  totalOutstanding: number;
  totalVoidedAmount: number;
  paidPercentage: number;
  voidedPercentage: number;
  voidedCountsAgainstPerformancePercentage: number;
  collectionRatePercentage: number;
}

interface CitationStatusBreakdown {
  labels: string[];
  counts: number[];
  colors: string[];
}

interface OfficerPerformanceStatistics {
  officerId: number;
  officerName: string;
  totalCitations: number;
  paidCitations: number;
  voidedCitations: number;
  voidedCountsAgainstPerformance: number;
  revenue: number;
  performanceScore: number;
}

interface CitationTimeSeries {
  labels: string[];
  values: number[];
}

interface CitationsByInfringementType {
  infringementTypeId: number;
  infringementTypeName: string;
  count: number;
  revenue: number;
}

interface Officer {
  id: number;
  badgeNumber: string;
  displayName: string;
}

@Component({
    selector: 'app-citation-statistics',
    templateUrl: './citation-statistics.component.html',
    styleUrls: ['./citation-statistics.component.scss'],
    standalone: false
})
export class CitationStatisticsComponent extends Busyable implements OnInit {
  
  public statistics: CitationStatisticsResponse | null = null;
  public officers: Officer[] = [];
  
  // Filter values
  public selectedOfficerId: number | null = null;
  public startDate: string;
  public endDate: string;
  public groupBy: string = 'day';
  
  // Chart data
  public statusBreakdownChartData: ChartDataResponse | null = null;
  public revenueChartData: ChartDataResponse | null = null;
  public citationsChartData: ChartDataResponse | null = null;
  public infringementTypeChartData: ChartDataResponse | null = null;
  public officerPerformanceChartData: ChartDataResponse | null = null;
  
  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    
    // Default date range: last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    this.startDate = thirtyDaysAgo.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
  }
  
  async ngOnInit(): Promise<void> {
    await this.loadOfficers();
    await this.loadStatistics();
  }
  
  private async loadOfficers(): Promise<void> {
    try {
      const response = await this.apiService.Post<any>('enforcement/officers/searches', {
        pageSize: 1000,
        pageNumber: 1
      });
      // API returns Results array (IDataListResponse structure)
      const officerData = response.Results || response.results || response.Data || response.data || [];
      this.officers = officerData.map((o: any) => ({
        id: o.Id ?? o.id,
        badgeNumber: o.BadgeNumber ?? o.badgeNumber,
        displayName: (o.BadgeNumber ?? o.badgeNumber) || `Officer ${o.Id ?? o.id}`
      }));
    } catch (error) {
      console.error('Error loading officers:', error);
    }
  }
  
  public async loadStatistics(): Promise<void> {
    this.Loading();
    
    try {
      const request = {
        officerId: this.selectedOfficerId,
        startDate: this.startDate ? new Date(this.startDate).toISOString() : null,
        endDate: this.endDate ? new Date(this.endDate).toISOString() : null,
        groupBy: this.groupBy
      };
      
      const response = await this.apiService.Post<any>(
        'enforcement/citations/statistics',
        request
      );
      
      // Map the response to handle PascalCase from C# API
      this.statistics = this.mapResponse(response);
      
      this.buildChartData();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading citation statistics:', error);
    } finally {
      this.StopLoading();
    }
  }
  
  private mapResponse(response: any): CitationStatisticsResponse {
    // Handle both PascalCase (C#) and camelCase responses
    const summary = response.summary || response.Summary;
    const statusBreakdown = response.statusBreakdown || response.StatusBreakdown;
    const officerPerformance = response.officerPerformance || response.OfficerPerformance || [];
    const revenueOverTime = response.revenueOverTime || response.RevenueOverTime;
    const citationsOverTime = response.citationsOverTime || response.CitationsOverTime;
    const citationsByInfringementType = response.citationsByInfringementType || response.CitationsByInfringementType || [];
    
    return {
      summary: {
        totalCitations: summary?.totalCitations ?? summary?.TotalCitations ?? 0,
        totalPaid: summary?.totalPaid ?? summary?.TotalPaid ?? 0,
        totalVoided: summary?.totalVoided ?? summary?.TotalVoided ?? 0,
        totalVoidedCountsAgainstPerformance: summary?.totalVoidedCountsAgainstPerformance ?? summary?.TotalVoidedCountsAgainstPerformance ?? 0,
        totalPending: summary?.totalPending ?? summary?.TotalPending ?? 0,
        totalRevenue: summary?.totalRevenue ?? summary?.TotalRevenue ?? 0,
        totalOutstanding: summary?.totalOutstanding ?? summary?.TotalOutstanding ?? 0,
        totalVoidedAmount: summary?.totalVoidedAmount ?? summary?.TotalVoidedAmount ?? 0,
        paidPercentage: summary?.paidPercentage ?? summary?.PaidPercentage ?? 0,
        voidedPercentage: summary?.voidedPercentage ?? summary?.VoidedPercentage ?? 0,
        voidedCountsAgainstPerformancePercentage: summary?.voidedCountsAgainstPerformancePercentage ?? summary?.VoidedCountsAgainstPerformancePercentage ?? 0,
        collectionRatePercentage: summary?.collectionRatePercentage ?? summary?.CollectionRatePercentage ?? 0
      },
      statusBreakdown: {
        labels: statusBreakdown?.labels ?? statusBreakdown?.Labels ?? [],
        counts: statusBreakdown?.counts ?? statusBreakdown?.Counts ?? [],
        colors: statusBreakdown?.colors ?? statusBreakdown?.Colors ?? []
      },
      officerPerformance: (officerPerformance || []).map((o: any) => ({
        officerId: o.officerId ?? o.OfficerId ?? 0,
        officerName: o.officerName ?? o.OfficerName ?? '',
        totalCitations: o.totalCitations ?? o.TotalCitations ?? 0,
        paidCitations: o.paidCitations ?? o.PaidCitations ?? 0,
        voidedCitations: o.voidedCitations ?? o.VoidedCitations ?? 0,
        voidedCountsAgainstPerformance: o.voidedCountsAgainstPerformance ?? o.VoidedCountsAgainstPerformance ?? 0,
        revenue: o.revenue ?? o.Revenue ?? 0,
        performanceScore: o.performanceScore ?? o.PerformanceScore ?? 100
      })),
      revenueOverTime: {
        labels: revenueOverTime?.labels ?? revenueOverTime?.Labels ?? [],
        values: revenueOverTime?.values ?? revenueOverTime?.Values ?? []
      },
      citationsOverTime: {
        labels: citationsOverTime?.labels ?? citationsOverTime?.Labels ?? [],
        values: citationsOverTime?.values ?? citationsOverTime?.Values ?? []
      },
      citationsByInfringementType: (citationsByInfringementType || []).map((t: any) => ({
        infringementTypeId: t.infringementTypeId ?? t.InfringementTypeId ?? 0,
        infringementTypeName: t.infringementTypeName ?? t.InfringementTypeName ?? '',
        count: t.count ?? t.Count ?? 0,
        revenue: t.revenue ?? t.Revenue ?? 0
      }))
    };
  }
  
  public onFilterChange(): void {
    this.loadStatistics();
  }
  
  private buildChartData(): void {
    if (!this.statistics) return;
    
    // Status breakdown pie chart - Filter out zero values
    const nonZeroIndices = this.statistics.statusBreakdown.counts
      .map((count, index) => ({ count, index }))
      .filter(item => item.count > 0);
    
    this.statusBreakdownChartData = {
      labels: nonZeroIndices.map(item => this.statistics!.statusBreakdown.labels[item.index]),
      datasets: [{
        label: 'Citation Status',
        data: nonZeroIndices.map(item => item.count),
        backgroundColor: nonZeroIndices.map(item => this.statistics!.statusBreakdown.colors[item.index]),
        displayColor: '#006400',
        targetType: 'Citation',
        id: 1
      }],
      stacked: false
    };
    
    // Revenue over time line chart
    this.revenueChartData = {
      labels: this.statistics.revenueOverTime.labels,
      datasets: [{
        label: 'Revenue',
        data: this.statistics.revenueOverTime.values,
        displayColor: '#28a745',
        type: 'line',
        targetType: 'Revenue',
        id: 1
      }],
      stacked: false
    };
    
    // Citations over time bar chart
    this.citationsChartData = {
      labels: this.statistics.citationsOverTime.labels,
      datasets: [{
        label: 'Citations',
        data: this.statistics.citationsOverTime.values,
        displayColor: '#006400',
        type: 'bar',
        targetType: 'Citation',
        id: 1
      }],
      stacked: false
    };
    
    // Citations by infringement type pie chart
    const topInfringementTypes = this.statistics.citationsByInfringementType.slice(0, 8);
    this.infringementTypeChartData = {
      labels: topInfringementTypes.map(t => t.infringementTypeName),
      datasets: [{
        label: 'Infringement Types',
        data: topInfringementTypes.map(t => t.count),
        backgroundColor: ['#006400', '#19BE19', '#228B22', '#32CD32', '#3CB371', '#2E8B57', '#20B2AA', '#008B8B'],
        displayColor: '#006400',
        targetType: 'InfringementType',
        id: 1
      }],
      stacked: false
    };
    
    // Officer performance bar chart
    if (this.statistics.officerPerformance.length > 0) {
      const topOfficers = this.statistics.officerPerformance.slice(0, 10);
      this.officerPerformanceChartData = {
        labels: topOfficers.map(o => o.officerName),
        datasets: [
          {
            label: 'Citations Issued',
            data: topOfficers.map(o => o.totalCitations),
            displayColor: '#006400',
            type: 'bar',
            targetType: 'Officer',
            id: 1
          },
          {
            label: 'Performance Score',
            data: topOfficers.map(o => o.performanceScore),
            displayColor: '#17a2b8',
            type: 'line',
            targetType: 'Officer',
            id: 2
          }
        ],
        stacked: false
      };
    }
  }
  
  public formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  
  public formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
  }
  
  public formatPercent(value: number): string {
    return `${value.toFixed(1)}%`;
  }
}

